/* ─────────────────────────────────────────────────────────────────────
   End Card Settings — Live Preview Editor for the 360 Boomerang
   ─────────────────────────────────────────────────────────────────────
   The host's first taste of "this is MY event". Live preview is the
   centrepiece — controls are minimal and supportive. Auto-saves on
   every change with debounce, no save button, no friction.

   Per the user's brief:
     • Preview = big, central, always visible
     • Controls = minimal, supportive
     • No labels above preview
     • Auto-save → no save button
     • Live update on type / colour pick / logo swap

   The preview here is HTML/CSS — it mirrors the server-side PIL
   render closely enough that a host can trust what they see. The
   actual boomerang end card is still rendered server-side from the
   saved settings.
   ───────────────────────────────────────────────────────────────────── */

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Theme accent palette — host-friendly preset colours per theme. Custom
// hex always wins via the colour picker.
const PRESET_PALETTE = [
  '#D6B25E',   // wedding gold
  '#E8C170',   // champagne
  '#4FC3F7',   // school blue
  '#1E5BB8',   // navy
  '#00D4FF',   // party cyan
  '#B89762',   // electric purple
  '#FF4D6D',   // hot pink
  '#22C55E',   // signal green
];

const SAVE_DEBOUNCE_MS = 600;

const EndCardSettingsPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState(null);   // theme + event_name + theme defaults
  const [settings, setSettings] = useState({
    endcard_message: '',
    accent_color: '',
    endcard_show_branding: true,
    endcard_logo_url: null,
  });
  const [showCustomColor, setShowCustomColor] = useState(false);
  const fileInputRef = useRef(null);

  // ── Initial load ────────────────────────────────────────────────────
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/events/${eventId}/endcard`, {
          withCredentials: true,
        });
        if (!active) return;
        setMeta({
          event_name: r.data.event_name,
          event_type: r.data.event_type,
          theme: r.data.theme,
          theme_default_accent: r.data.theme_default_accent,
          theme_default_message: r.data.theme_default_message,
        });
        setSettings({
          endcard_message: r.data.endcard_message || '',
          accent_color: r.data.accent_color || '',
          endcard_show_branding: r.data.endcard_show_branding !== false,
          endcard_logo_url: r.data.endcard_logo_url || null,
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[endcard-settings] load failed', e);
        setError(e?.response?.data?.detail || 'Could not load settings.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [eventId]);

  // ── Debounced auto-save ─────────────────────────────────────────────
  // Mirror local edits to the backend without a save button. The user
  // sees the preview update INSTANTLY (HTML is local), the save just
  // catches up in the background — invisible if it succeeds.
  const initialLoadDoneRef = useRef(false);
  useEffect(() => {
    if (loading) return;
    if (!initialLoadDoneRef.current) {
      initialLoadDoneRef.current = true;
      return;     // skip the first run (post-load reflect)
    }
    const t = setTimeout(async () => {
      setSaving(true);
      try {
        await axios.put(`${API}/events/${eventId}/endcard`, settings, {
          withCredentials: true,
        });
      } catch (e) {
        console.error('[endcard-settings] save failed', e);
        setError('Save failed. Try again.');
      } finally {
        setSaving(false);
      }
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [settings, eventId, loading]);

  // ── Logo upload ─────────────────────────────────────────────────────
  const onLogoFile = useCallback(async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/events/${eventId}/endcard/logo`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true },
      );
      setSettings((s) => ({ ...s, endcard_logo_url: r.data.endcard_logo_url }));
    } catch (e) {
      console.error('[endcard-settings] logo upload failed', e);
      setError(e?.response?.data?.detail || 'Logo upload failed.');
    } finally {
      setSaving(false);
    }
  }, [eventId]);

  const onLogoClear = useCallback(() => {
    setSettings((s) => ({ ...s, endcard_logo_url: null }));
  }, []);

  // ── Effective values for the preview ────────────────────────────────
  const effectiveAccent = settings.accent_color || meta?.theme_default_accent || '#D6B25E';
  const effectiveMessage = settings.endcard_message || meta?.theme_default_message || 'Thanks for celebrating ✦';
  const titleText = (meta?.event_name || 'Your event').toUpperCase();

  if (loading) {
    return (
      <div data-testid="endcard-settings-loading" style={S.loading}>
        <div style={S.spinner} />
      </div>
    );
  }

  return (
    <div data-testid="endcard-settings-page" style={S.page}>
      <header style={S.header}>
        <button
          data-testid="endcard-settings-back"
          onClick={() => navigate(-1)}
          style={S.backBtn}
        >
          ← Back
        </button>
        <div style={S.headerTitleWrap}>
          <h1 style={S.headerTitle}>End Card</h1>
          <p style={S.headerSubtitle}>Updates instantly · auto-saved</p>
        </div>
        <span data-testid="endcard-save-status" style={{
          ...S.saveBadge,
          opacity: saving ? 1 : 0.55,
          color: saving ? '#D4AF37' : 'rgba(255,255,255,0.42)',
        }}>
          {saving ? 'Saving…' : 'Saved'}
        </span>
      </header>

      <main style={S.main}>
        {/* ── Live preview card ─────────────────────────────────── */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
          style={S.previewWrap}
        >
          <div style={S.previewLabel}>
            <span style={{ opacity: 0.75 }}>Preview of your final video ending</span>
            <span style={{ ...S.shimmerBadge, color: effectiveAccent }}>✨ Live</span>
          </div>
          <EndCardPreview
            title={titleText}
            message={effectiveMessage}
            accent={effectiveAccent}
            logoUrl={settings.endcard_logo_url}
            showBranding={settings.endcard_show_branding}
            theme={meta?.theme}
          />
        </motion.section>

        {/* ── Controls ─────────────────────────────────────────── */}
        <section style={S.controls}>
          {/* Message */}
          <Field label="Message">
            <input
              data-testid="endcard-message-input"
              type="text"
              maxLength={80}
              value={settings.endcard_message}
              placeholder={meta?.theme_default_message || 'Thanks for celebrating ✦'}
              onChange={(e) => setSettings((s) => ({ ...s, endcard_message: e.target.value }))}
              style={S.input}
            />
          </Field>

          {/* Accent colour */}
          <Field label="Accent colour">
            <div style={S.swatchRow}>
              {PRESET_PALETTE.map((c) => (
                <button
                  key={c}
                  data-testid={`endcard-accent-${c.replace('#', '').toLowerCase()}`}
                  onClick={() => {
                    setSettings((s) => ({ ...s, accent_color: c }));
                    setShowCustomColor(false);
                  }}
                  style={{
                    ...S.swatch,
                    background: c,
                    border: settings.accent_color === c
                      ? '2px solid #fff'
                      : '2px solid rgba(255,255,255,0.12)',
                    transform: settings.accent_color === c ? 'scale(1.08)' : 'scale(1)',
                  }}
                  aria-label={`Accent ${c}`}
                />
              ))}
              <button
                data-testid="endcard-accent-custom-toggle"
                onClick={() => setShowCustomColor((v) => !v)}
                style={{
                  ...S.swatch,
                  background: 'transparent',
                  border: '2px dashed rgba(255,255,255,0.28)',
                  color: 'rgba(255,255,255,0.72)',
                  fontSize: 18,
                }}
                aria-label="Custom colour"
              >
                +
              </button>
            </div>
            <AnimatePresence>
              {showCustomColor && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  style={{ marginTop: 12 }}
                >
                  <input
                    data-testid="endcard-accent-custom-input"
                    type="color"
                    value={effectiveAccent}
                    onChange={(e) => setSettings((s) => ({ ...s, accent_color: e.target.value.toUpperCase() }))}
                    style={S.colorPicker}
                  />
                </motion.div>
              )}
            </AnimatePresence>
            {settings.accent_color && settings.accent_color !== meta?.theme_default_accent && (
              <button
                data-testid="endcard-accent-reset"
                onClick={() => setSettings((s) => ({ ...s, accent_color: '' }))}
                style={S.resetLink}
              >
                Reset to {meta?.theme} default ({meta?.theme_default_accent})
              </button>
            )}
          </Field>

          {/* Logo */}
          <Field label="Logo">
            <div style={S.logoRow}>
              <button
                data-testid="endcard-logo-upload"
                onClick={() => fileInputRef.current?.click()}
                style={S.logoUploadBtn}
              >
                {settings.endcard_logo_url ? 'Replace logo' : 'Upload logo'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => onLogoFile(e.target.files?.[0])}
                style={{ display: 'none' }}
              />
              {settings.endcard_logo_url && (
                <button
                  data-testid="endcard-logo-clear"
                  onClick={onLogoClear}
                  style={S.logoClearBtn}
                >
                  Use EventDrop logo
                </button>
              )}
            </div>
            <p style={S.helpText}>
              {settings.endcard_logo_url
                ? 'Your custom logo replaces the EventDrop logo on the end card.'
                : 'Optional — your school crest, brand mark, or couple monogram.'}
            </p>
          </Field>

          {/* Branding toggle */}
          <Field label="">
            <label style={S.toggleRow}>
              <input
                data-testid="endcard-show-branding-toggle"
                type="checkbox"
                checked={settings.endcard_show_branding}
                onChange={(e) => setSettings((s) => ({ ...s, endcard_show_branding: e.target.checked }))}
                style={S.checkbox}
              />
              <span style={S.toggleLabel}>Show "Book your event" CTA</span>
            </label>
            <p style={S.helpText}>
              When off, the end card hides the EventDrop logo + CTA. Just the message + accent.
            </p>
          </Field>
        </section>

        {error && (
          <div data-testid="endcard-error" style={S.errorBanner}>
            {error}
          </div>
        )}
      </main>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Live Preview — HTML/CSS mirror of the server-side PIL render
// ─────────────────────────────────────────────────────────────────────

const EndCardPreview = ({ title, message, accent, logoUrl, showBranding, theme }) => {
  const isWedding = theme === 'wedding_gold';
  const isPartyGradient = theme === 'party_gradient';
  const titleFont = isWedding
    ? '"Playfair Display", Georgia, "Times New Roman", serif'
    : 'Inter, -apple-system, BlinkMacSystemFont, sans-serif';

  return (
    <div data-testid="endcard-preview" style={{
      ...S.preview,
      // Subtle accent halo behind the card content — gives it the
      // "this matters" feel from the actual render.
      backgroundImage: `radial-gradient(circle at center, ${accent}22 0%, transparent 55%), radial-gradient(circle at center, ${accent}14 0%, transparent 70%)`,
    }}>
      {/* Subtle vignette so it reads as a video frame, not a UI box */}
      <div style={S.vignette} />

      {/* Tiny grain overlay */}
      <div style={S.grain} />

      <div style={S.previewContent}>
        <h2 data-testid="endcard-preview-title" style={{
          ...S.previewTitle,
          fontFamily: titleFont,
          letterSpacing: isWedding ? '0.10em' : '0.08em',
        }}>
          {title}
        </h2>

        <p data-testid="endcard-preview-message" style={S.previewMessage}>
          {message}
        </p>

        {showBranding && (
          <>
            {logoUrl ? (
              <img
                data-testid="endcard-preview-logo"
                src={logoUrl}
                alt="logo"
                style={S.previewLogoCustom}
              />
            ) : (
              <EventDropLogoMark accent={accent} />
            )}

            {isPartyGradient ? (
              <div style={{
                ...S.divider,
                background: `linear-gradient(90deg, ${accent} 0%, ${accent} 50%, ${shadeHex(accent, -20)} 50%, ${shadeHex(accent, -20)} 100%)`,
              }} />
            ) : (
              <motion.div
                key={accent}      // re-mount on accent change → animates
                initial={{ scaleX: 0.4, opacity: 0.5 }}
                animate={{ scaleX: 1, opacity: 1 }}
                transition={{ duration: 0.32, ease: 'easeOut' }}
                style={{ ...S.divider, background: accent }}
              />
            )}

            <div style={{ ...S.previewCta, color: accent }}>Book your event</div>
            <div style={S.previewCtaUrl}>eventdrop.co</div>
          </>
        )}
      </div>
    </div>
  );
};

// Tiny inline EventDrop wordmark — renders without needing the asset URL
// (the server-side render uses the bundled PNG; here we just convey the
// brand mark visually).
const EventDropLogoMark = ({ accent }) => (
  <div data-testid="endcard-preview-logo" style={S.brandMark}>
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 2 C8 8, 4 13, 4 17 a8 8 0 0 0 16 0 c0-4-4-9-8-15z"
        fill={accent}
        opacity="0.92"
      />
      <circle cx="12" cy="16" r="3.2" fill="#0B0B0F" />
      <circle cx="12" cy="16" r="1.6" fill={accent} />
    </svg>
    <span style={{ fontWeight: 700, fontSize: 15, color: '#FFFFFF', letterSpacing: '0.01em' }}>
      EventDrop
    </span>
  </div>
);

// Shade helper for the party_gradient two-tone divider.
function shadeHex(hex, percent) {
  const h = (hex || '').replace('#', '');
  if (h.length !== 6) return hex;
  const num = parseInt(h, 16);
  const amt = Math.round(2.55 * percent);
  const r = Math.max(0, Math.min(255, ((num >> 16) & 0xff) + amt));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + amt));
  const b = Math.max(0, Math.min(255, (num & 0xff) + amt));
  return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0').toUpperCase();
}

// ─────────────────────────────────────────────────────────────────────
// Tiny field wrapper — keeps the controls visually consistent
// ─────────────────────────────────────────────────────────────────────
const Field = ({ label, children }) => (
  <div style={S.field}>
    {label && <div style={S.fieldLabel}>{label}</div>}
    {children}
  </div>
);

// ─────────────────────────────────────────────────────────────────────
// Styles (mobile-first, dark theme — matches the booth aesthetic)
// ─────────────────────────────────────────────────────────────────────
const S = {
  page: {
    minHeight: '100vh',
    background: '#0B0B0F',
    color: '#fff',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  loading: {
    minHeight: '100vh', background: '#0B0B0F', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  spinner: {
    width: 36, height: 36,
    border: '2px solid rgba(255,255,255,0.10)',
    borderTopColor: '#D4AF37',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 16,
    padding: '18px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  backBtn: {
    background: 'transparent', border: '1px solid rgba(255,255,255,0.12)',
    color: 'rgba(255,255,255,0.86)', borderRadius: 999,
    padding: '8px 14px', fontSize: 13, cursor: 'pointer',
  },
  headerTitleWrap: { flex: 1, marginLeft: 4 },
  headerTitle: {
    margin: 0, fontSize: 18, fontWeight: 600, letterSpacing: '0.02em',
  },
  headerSubtitle: {
    margin: '2px 0 0 0', fontSize: 12, color: 'rgba(255,255,255,0.42)',
  },
  saveBadge: {
    fontSize: 12, letterSpacing: '0.06em',
    transition: 'opacity 0.3s ease, color 0.3s ease',
  },
  main: {
    maxWidth: 720, margin: '0 auto', padding: '24px 20px 60px',
    display: 'flex', flexDirection: 'column', gap: 28,
  },
  previewWrap: { display: 'flex', flexDirection: 'column', gap: 10 },
  previewLabel: {
    fontSize: 12, letterSpacing: '0.04em',
    color: 'rgba(255,255,255,0.55)',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  shimmerBadge: { fontSize: 11, fontWeight: 600 },

  preview: {
    position: 'relative', overflow: 'hidden',
    aspectRatio: '1 / 1',
    maxWidth: 420, width: '100%',
    margin: '0 auto',
    borderRadius: 20,
    background: '#0B0B0F',
    boxShadow: '0 22px 60px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  vignette: {
    position: 'absolute', inset: 0,
    background: 'radial-gradient(circle at center, transparent 55%, rgba(0,0,0,0.55) 100%)',
    pointerEvents: 'none',
  },
  grain: {
    position: 'absolute', inset: 0,
    background:
      "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22><filter id=%22n%22><feTurbulence baseFrequency=%220.9%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22 opacity=%220.5%22/></svg>')",
    opacity: 0.05, mixBlendMode: 'overlay', pointerEvents: 'none',
  },
  previewContent: {
    position: 'relative', zIndex: 1,
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 16, padding: 24, textAlign: 'center', width: '100%',
  },
  previewTitle: {
    margin: 0, fontSize: 26, fontWeight: 700,
    color: '#FFFFFF', textTransform: 'uppercase',
    lineHeight: 1.18, maxWidth: '90%',
    overflowWrap: 'break-word',
  },
  previewMessage: {
    margin: 0, fontSize: 14,
    color: 'rgba(255,255,255,0.82)',
    fontWeight: 400, letterSpacing: '0.02em',
  },
  brandMark: {
    display: 'flex', alignItems: 'center', gap: 8,
    marginTop: 6,
  },
  previewLogoCustom: {
    maxWidth: 110, maxHeight: 50, marginTop: 4,
    objectFit: 'contain',
  },
  divider: {
    width: 64, height: 2, borderRadius: 2,
    transformOrigin: 'center',
    transition: 'background 0.32s ease',
  },
  previewCta: {
    margin: 0, fontSize: 12, fontWeight: 600,
    letterSpacing: '0.04em',
    transition: 'color 0.32s ease',
  },
  previewCtaUrl: {
    fontSize: 11, color: 'rgba(255,255,255,0.45)',
    letterSpacing: '0.02em',
  },

  controls: {
    display: 'flex', flexDirection: 'column', gap: 22,
  },
  field: { display: 'flex', flexDirection: 'column', gap: 10 },
  fieldLabel: {
    fontSize: 12, letterSpacing: '0.06em',
    color: 'rgba(255,255,255,0.55)',
    textTransform: 'uppercase',
  },
  input: {
    width: '100%', boxSizing: 'border-box',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 10,
    padding: '12px 14px',
    color: '#fff', fontSize: 15,
    outline: 'none',
    transition: 'border-color 0.2s ease',
  },
  swatchRow: { display: 'flex', flexWrap: 'wrap', gap: 10 },
  swatch: {
    width: 36, height: 36, borderRadius: 999,
    cursor: 'pointer', transition: 'transform 0.18s ease, border-color 0.18s ease',
  },
  colorPicker: {
    width: 72, height: 36, border: 'none',
    background: 'transparent', cursor: 'pointer',
  },
  resetLink: {
    background: 'transparent', border: 'none', padding: 0,
    color: 'rgba(255,255,255,0.5)', fontSize: 12,
    textDecoration: 'underline', cursor: 'pointer',
    marginTop: 6, alignSelf: 'flex-start',
  },
  logoRow: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  logoUploadBtn: {
    background: '#fff', color: '#0B0B0F',
    border: 'none', borderRadius: 999,
    padding: '10px 18px', fontSize: 13, fontWeight: 600,
    cursor: 'pointer',
  },
  logoClearBtn: {
    background: 'transparent',
    color: 'rgba(255,255,255,0.62)',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: 999,
    padding: '10px 18px', fontSize: 13,
    cursor: 'pointer',
  },
  toggleRow: {
    display: 'flex', alignItems: 'center', gap: 10,
    cursor: 'pointer', userSelect: 'none',
  },
  checkbox: { width: 18, height: 18, accentColor: '#D4AF37' },
  toggleLabel: { fontSize: 14, color: 'rgba(255,255,255,0.86)' },
  helpText: {
    margin: 0, fontSize: 12,
    color: 'rgba(255,255,255,0.42)',
    letterSpacing: '0.01em', lineHeight: 1.5,
  },
  errorBanner: {
    background: 'rgba(255,77,109,0.12)',
    border: '1px solid rgba(255,77,109,0.32)',
    color: '#FF8FA3',
    borderRadius: 10, padding: '12px 14px', fontSize: 13,
  },
};

export default EndCardSettingsPage;
